import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

import HelplineImg from "../Assets/WomenAndMothers.webp";
import FoodImg from "../Assets/497398309_722259213462727_8845438689969593985_n.jpg";
import EventsImg from "../Assets/fd0fa024-50c4-4112-96fe-7328b40bb0ad.jpg";
import ParticipantsImg from "../Assets/participants.webp";
import VolunteersImg from "../Assets/348569580_226072346797853_6675489796423688377_n.jpg";

const ease = [0.22, 1, 0.36, 1];

const fadeInUp = {
  initial: {
    opacity: 0,
    y: 48,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.85,
      ease,
    },
  },
};

const fadeInLeft = {
  initial: {
    opacity: 0,
    x: -45,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease,
    },
  },
};

const fadeInRight = {
  initial: {
    opacity: 0,
    x: 45,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease,
    },
  },
};

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

// ---------------------------
// ANIMATED NUMBER COMPONENT
// ---------------------------
const AnimatedNumber = ({ targetValue, suffix = "+", trigger }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!trigger) return;

    const duration = 1400;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      setCount(Math.floor(easedProgress * targetValue));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(targetValue);
      }
    };

    requestAnimationFrame(animate);
  }, [targetValue, trigger]);

  return (
    <span>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

// ---------------------------
// IMPACT DATA
// ---------------------------
const impactData = [
  {
    title: "Helpline",
    shortTitle: "Helpline",
    image: HelplineImg,
    value: 50,
    suffix: "+",
    label: "Crisis Support",
    description:
      "Our mental health helpline has supported over 50 individuals in times of crisis and need.",
  },
  {
    title: "Food Hampers Distributed",
    shortTitle: "Food Hampers",
    image: FoodImg,
    value: 12000,
    suffix: "+",
    label: "Food Security",
    description:
      "We have distributed over 12,000 food hampers to families across the community facing food insecurity.",
  },
  {
    title: "Events",
    shortTitle: "Events",
    image: EventsImg,
    value: 50,
    suffix: "+",
    label: "Community Connection",
    description:
      "Over 50 community-building events have brought people together, fostering connection and wellness.",
  },
  {
    title: "Participants",
    shortTitle: "Participants",
    image: ParticipantsImg,
    value: 1500,
    suffix: "+",
    label: "Program Reach",
    description:
      "More than 1,500 participants have engaged with our programs, amplifying impact and hope.",
  },
  {
    title: "Volunteers",
    shortTitle: "Volunteers",
    image: VolunteersImg,
    value: 150,
    suffix: "+",
    label: "Volunteer Support",
    description:
      "Over 150 volunteers have generously donated their time to support our mission.",
  },
];

// ---------------------------
// IMPACT ITEM COMPONENT
// ---------------------------
const ImpactItem = ({ item, index }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.25,
  });

  const contentAnimation =
    index % 2 === 0 ? fadeInLeft : fadeInRight;

  const numberAnimation =
    index % 2 === 0 ? fadeInRight : fadeInLeft;

  return (
    <motion.section
      ref={ref}
      initial="initial"
      animate={inView ? "animate" : "initial"}
      className="relative min-h-[82vh] md:min-h-screen flex items-center px-6 sm:px-10 lg:px-16 py-24 md:py-28 overflow-hidden"
    >
      {/* BACKGROUND IMAGE */}
      <motion.div
        initial={{ scale: 1.06 }}
        animate={inView ? { scale: 1 } : { scale: 1.06 }}
        transition={{
          duration: 1.6,
          ease,
        }}
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${item.image})`,
        }}
      />

      {/* PHOTO OVERLAY */}
      <div className="absolute inset-0 bg-black/48" />

      <div
        className={`absolute inset-0 ${
          index % 2 === 0
            ? "bg-gradient-to-r from-black/80 via-black/50 to-black/20"
            : "bg-gradient-to-l from-black/80 via-black/50 to-black/20"
        }`}
      />

      {/* SOFT SECTION BLENDING */}
      <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-[#EAF6F4]/25 to-transparent dark:from-[#141414]/25" />

      <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#EAF6F4]/25 to-transparent dark:from-[#141414]/25" />

      <div className="relative z-10 max-w-7xl mx-auto w-full">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center ${
            index % 2 === 1
              ? "lg:[&>*:first-child]:order-2"
              : ""
          }`}
        >
          {/* TEXT */}
          <motion.div variants={contentAnimation}>
            <p
              className="text-xs sm:text-sm uppercase tracking-[0.28em] font-bold text-[#E4CE42] mb-6"
              style={{
                textShadow: "0 2px 8px rgba(0,0,0,0.75)",
              }}
            >
              Impact Area 0{index + 1} · {item.label}
            </p>

            <h2
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.04] tracking-[-0.04em]"
              style={{
                textShadow:
                  "0 3px 4px rgba(0,0,0,0.8), 0 10px 24px rgba(0,0,0,0.4)",
              }}
            >
              {item.title}
            </h2>

            <p
              className="mt-8 text-lg md:text-xl text-white/90 leading-relaxed max-w-2xl"
              style={{
                textShadow: "0 2px 8px rgba(0,0,0,0.7)",
              }}
            >
              {item.description}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="/contact"
                className="inline-flex min-h-[54px] items-center justify-center bg-[#D1BD3D] hover:bg-[#E0CB4A] text-black px-8 py-4 text-base sm:text-lg font-bold transition-all duration-300 shadow-[0_8px_24px_rgba(0,0,0,0.28)] hover:-translate-y-1"
              >
                Get Involved
              </a>

              <a
                href="/services"
                className="inline-flex min-h-[54px] items-center justify-center border border-white/55 bg-black/15 hover:bg-white hover:text-[#163C3B] text-white px-8 py-4 text-base sm:text-lg font-bold backdrop-blur-[3px] transition-all duration-300 hover:-translate-y-1"
              >
                View Programs
              </a>
            </div>
          </motion.div>

          {/* NUMBER */}
          <motion.div
            variants={numberAnimation}
            className={`flex ${
              index % 2 === 0
                ? "justify-center lg:justify-end"
                : "justify-center lg:justify-start"
            }`}
          >
            <div className="w-full max-w-[470px] border-t border-b border-white/35 py-10 md:py-14 text-center">
              <p
                className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-white/65 mb-6"
                style={{
                  textShadow: "0 2px 6px rgba(0,0,0,0.6)",
                }}
              >
                Community Impact
              </p>

              <div
                className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-extrabold text-[#E4CE42] tracking-[-0.05em]"
                style={{
                  textShadow:
                    "0 3px 5px rgba(0,0,0,0.8), 0 12px 28px rgba(0,0,0,0.45)",
                }}
              >
                <AnimatedNumber
                  targetValue={item.value}
                  suffix={item.suffix}
                  trigger={inView}
                />
              </div>

              <p
                className="mt-5 text-2xl md:text-3xl font-bold text-white"
                style={{
                  textShadow: "0 2px 8px rgba(0,0,0,0.7)",
                }}
              >
                {item.shortTitle}
              </p>

              <div className="mt-8 h-[3px] bg-white/20 overflow-hidden">
                <motion.div
                  initial={{ width: "0%" }}
                  animate={
                    inView
                      ? { width: "100%" }
                      : { width: "0%" }
                  }
                  transition={{
                    duration: 1.35,
                    ease,
                  }}
                  className="h-full bg-[#D8C442]"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// ---------------------------
// MAIN IMPACT COMPONENT
// ---------------------------
export default function Impact() {
  return (
    <main className="-mt-10 bg-[#F3FBFA] dark:bg-[#141414] text-[#163C3B] dark:text-white font-sans overflow-hidden transition-colors duration-300">
      {/* PAGE HERO */}
      <section className="relative px-6 sm:px-10 lg:px-16 pt-32 md:pt-40 pb-24 md:pb-32 bg-[#F3FBFA] dark:bg-[#141414]">
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
          className="relative max-w-6xl mx-auto text-center flex flex-col items-center"
        >
          <motion.p
            variants={fadeInUp}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-6"
          >
            Our Impact
          </motion.p>

          <motion.h1
            variants={fadeInUp}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.4rem] font-extrabold text-[#163C3B] dark:text-white leading-[1.02] tracking-[-0.045em]"
            style={{
              textShadow:
                "0 2px 3px rgba(17,63,61,0.15), 0 8px 22px rgba(17,63,61,0.06)",
            }}
          >
            Every number represents
            <span className="block mt-3 text-[#A18E1E] dark:text-[#E2CD42]">
              a person and a story.
            </span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="mt-8 text-lg sm:text-xl md:text-2xl text-[#476564] dark:text-gray-300 leading-relaxed max-w-3xl"
          >
            EthioCare's work is rooted in care, dignity, and meaningful
            community connection.
          </motion.p>
        </motion.div>
      </section>

      {/* QUICK IMPACT STATS STRIP */}
      <section className="px-6 sm:px-10 lg:px-16 pb-20 md:pb-28 bg-[#F3FBFA] dark:bg-[#141414]">
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
          className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-5 border-t border-l border-[#B6D3CF] dark:border-zinc-700"
        >
          {impactData.map((item, index) => (
            <motion.div
              key={item.title}
              variants={fadeInUp}
              className={`min-h-[170px] px-5 py-8 sm:p-8 border-r border-b border-[#B6D3CF] dark:border-zinc-700 flex flex-col justify-between ${
                index === impactData.length - 1
                  ? "col-span-2 lg:col-span-1"
                  : ""
              }`}
            >
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#8F8120] dark:text-[#CDB937]">
                0{index + 1}
              </p>

              <div className="mt-8">
                <p
                  className="text-3xl sm:text-4xl font-extrabold text-[#163C3B] dark:text-white"
                  style={{
                    textShadow:
                      "0 2px 3px rgba(17,63,61,0.1)",
                  }}
                >
                  {item.value.toLocaleString()}
                  {item.suffix}
                </p>

                <p className="mt-2 text-sm sm:text-base font-semibold text-[#577371] dark:text-gray-300">
                  {item.shortTitle}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* FULL IMPACT SECTIONS */}
      {impactData.map((item, index) => (
        <ImpactItem
          key={item.title}
          item={item}
          index={index}
        />
      ))}

      {/* FINAL CTA */}
      <section className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 bg-[#F3FBFA] dark:bg-[#141414]">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{
            once: true,
            amount: 0.25,
          }}
          variants={fadeInUp}
          className="max-w-7xl mx-auto"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-24 items-start">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-5">
                Continue The Work
              </p>

              <h2
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#163C3B] dark:text-white leading-[1.08] tracking-[-0.035em]"
                style={{
                  textShadow:
                    "0 2px 3px rgba(17,63,61,0.14), 0 7px 18px rgba(17,63,61,0.05)",
                }}
              >
                Help us continue this work.
              </h2>
            </div>

            <div className="border-t border-[#A7CBC6] dark:border-zinc-700 pt-8">
              <p className="text-xl md:text-2xl text-[#365A58] dark:text-gray-300 leading-relaxed">
                Volunteer, partner, donate, or connect someone with support.
                Every contribution helps strengthen community care.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href="/contact"
                  className="inline-flex min-h-[56px] items-center justify-center bg-[#D1BD3D] hover:bg-[#E0CB4A] text-black px-8 py-4 text-lg font-bold transition-all duration-300 hover:-translate-y-1"
                >
                  Contact EthioCare
                </a>

                <a
                  href="/services"
                  className="inline-flex min-h-[56px] items-center justify-center border border-[#759A96] dark:border-white/35 text-[#163C3B] dark:text-white hover:bg-[#163C3B] hover:text-white dark:hover:bg-white dark:hover:text-[#163C3B] px-8 py-4 text-lg font-bold transition-all duration-300 hover:-translate-y-1"
                >
                  View Programs
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#B6D3CF] dark:border-zinc-800 bg-[#EAF6F4] dark:bg-[#111111] px-6 py-10 text-center">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-extrabold text-[#163C3B] dark:text-[#CDB937] mb-3">
            EthioCare
          </h3>

          <p className="text-[#577371] dark:text-gray-400 max-w-2xl mx-auto mb-6 leading-relaxed">
            Supporting communities through culturally appropriate mental health
            awareness, family wellness, and compassionate connection.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-5 mb-6 text-sm font-semibold">
            <a
              href="/"
              className="text-[#577371] dark:text-gray-300 hover:text-[#8F8120] dark:hover:text-[#CDB937] transition-colors"
            >
              Home
            </a>

            <a
              href="/services"
              className="text-[#577371] dark:text-gray-300 hover:text-[#8F8120] dark:hover:text-[#CDB937] transition-colors"
            >
              Programs
            </a>

            <a
              href="/about"
              className="text-[#577371] dark:text-gray-300 hover:text-[#8F8120] dark:hover:text-[#CDB937] transition-colors"
            >
              About
            </a>

            <a
              href="/contact"
              className="text-[#577371] dark:text-gray-300 hover:text-[#8F8120] dark:hover:text-[#CDB937] transition-colors"
            >
              Contact
            </a>
          </div>

          <p className="text-sm text-[#68817F] dark:text-gray-400">
            Copyright © 2024 EthioCare - All Rights Reserved.
          </p>

          <p className="mt-1 text-sm text-[#68817F] dark:text-gray-400">
            Powered by{" "}
            <span className="text-[#8F8120] dark:text-[#CDB937] font-semibold">
              EthioCare
            </span>
          </p>
        </div>
      </footer>
    </main>
  );
}